import { t } from 'i18next';
import * as yup from 'yup';

import {
  brainsEndpoints,
  bundlesEndpoints,
  businessHoursEndpoints,
  collectionsEndpoints,
} from '@/api/endpoints';

import { debouncedCheckNameAvailability } from '../debouncedCheckNames';
import { LENGTH_XS, LENGTH_M, LENGTH_L, LENGTH_XXXL } from './constants';
import {
  contextRefRegExp,
  noUrlPattern,
  urlPattern,
  contextUrl,
  contextVariableWithBraces,
} from './patterns';

/**
 * Creates a Yup schema for validating name fields with uniqueness check
 *
 * @param opts.min - Minimum length (default: 1)
 * @param opts.max - Maximum length (default: LENGTH_XS = 32)
 * @param opts.getEndpoint - Function that takes name and optional params, returns the check endpoint URL
 * @param opts.parentParams - Optional parent parameters to pass to getEndpoint (e.g., { account_id: '123' })
 * @param opts.excludeName - Optional name to exclude from uniqueness check (for edit mode, to skip validation if name unchanged)
 *
 * @example
 * // For brain names
 * const schema = createNameFieldSchema({
 *   getEndpoint: brainsEndpoints.checkName
 * });
 *
 * @example
 * // For edit mode with excludeName
 * const schema = createNameFieldSchema({
 *   getEndpoint: (name) => rulesEndpoints.checkName(deskId, name, { desk_id: deskId }),
 *   excludeName: currentName
 * });
 *
 */
export const createNameFieldSchema = (opts: {
  getEndpoint: (name: string, parentParams?: Record<string, string>) => string;
  min?: number;
  max?: number;
  parentParams?: Record<string, string>;
  excludeName?: string;
}) => {
  const min = opts?.min ?? 1;
  const max = opts?.max ?? LENGTH_XS;

  return yup
    .string()
    .trim()
    .required(() => t('validation.required'))
    .min(min, () => t('validation.at_least', { 0: min }))
    .max(max, () => t('validation.less_than', { 0: max }))
    .test(
      'unique-name',
      () => t('validation.name_already_exists'),
      async (value, ctx) => {
        const trimmedName = value?.trim();
        if (!trimmedName) {
          // Same `type` as `.required()`, so callers can tell an empty field from a taken name.
          return ctx.createError({ message: t('validation.required'), type: 'required' });
        }

        // Don't run the 500ms-debounced network check for a name that already
        // fails the cheap .min/.max length rules. Otherwise yup's single
        // validation promise resolves only after this async test settles, so
        // the length error surfaces a keystroke late.
        if (trimmedName.length < min || trimmedName.length > max) {
          return true;
        }

        // Skip validation if the name hasn't changed (edit mode)
        if (opts.excludeName && trimmedName.toLowerCase() === opts.excludeName.toLowerCase()) {
          return true;
        }

        try {
          const endpoint = opts.getEndpoint(trimmedName, opts.parentParams ?? {});
          const { available } = await debouncedCheckNameAvailability(endpoint);
          return available;
        } catch (error) {
          return ctx.createError({ message: error.message });
        }
      }
    )
    .label(t('common.name'));
};

/**
 * Creates a brain name schema with optional excludeName for edit mode
 * @param excludeName - The current brain name to exclude from validation (for edit mode)
 */
export const createBrainNameSchema = (excludeName?: string) =>
  yup
    .object()
    .shape({
      name: createNameFieldSchema({ getEndpoint: brainsEndpoints.checkName, excludeName }),
    });

export const createCollectionSchema = (isEditMode?: boolean, currentName?: string) => {
  return yup
    .object()
    .shape({
      name: createNameFieldSchema({
        getEndpoint: collectionsEndpoints.checkName,
        excludeName: currentName,
      }),
      language: yup.string().required(() => t('validation.required')),
      start_with: isEditMode
        ? yup.string().notRequired()
        : yup.string().required(() => t('validation.required')),
    });
};

export const businessHoursSchema = (deskId: string, currentName?: string) =>
  yup
    .object({
      businessHourName: createNameFieldSchema({
        getEndpoint: (name) => businessHoursEndpoints.checkName(deskId, name, { desk_id: deskId }),
        min: 2,
        max: 24,
        excludeName: currentName,
      }),
      holidays: yup
        .array()
        .of(
          yup.object({
            holidayName: yup
              .string()
              .trim()
              .min(2, 'Should be at least 2 characters long')
              .max(24, 'Should be less or equal than 24 characters long')
              .required('This is a required field'),
            dates: yup.object({
              startDate: yup.string().required(),
              endDate: yup.string().required(),
            }),
          })
        )
        .test('all-holidays-have-names', 'All holidays must have names', function (value) {
          if (!value || value.length === 0) {
            return true;
          } // No holidays is valid
          return value.every((holiday) => holiday.holidayName?.trim());
        }),
    })
    .required();

/**
 * Yup schema version for bundle validation with excludeName support
 * This demonstrates how to use createNameFieldSchema with the excludeName option for edit mode
 *
 * @param deskId - The desk ID for the bundle
 * @param currentName - Optional current name to exclude from uniqueness check (for edit mode)
 * @returns Yup object schema for bundle validation
 *
 * @example
 * // For create mode (as used in ModalBundleCreation)
 * const schema = createBundleSchema(deskId);
 *
 * @example
 * // For edit mode
 * const schema = createBundleSchema(deskId, currentBundleName);
 */
export const createBundleSchema = (deskId: string, currentName?: string) =>
  yup.object({
    name: createNameFieldSchema({
      getEndpoint: (name) => bundlesEndpoints.checkName(deskId, name, { desk_id: deskId }),
      excludeName: currentName,
    }),
    description: yup.string().max(LENGTH_M),
  });

// Schema generator for the EditableText input fields
export const getEditableTextValidationSchema = (
  maxLength: number,
  restrictedValues: Array<string>,
  label: string,
  patternValidation?: RegExp // New parameter to control regex validation
) => {
  let schema = yup
    .string()
    .trim()
    .required(() => t('validation.required'))
    .min(2, () => t('validation.at_least', { 0: 2 }))
    .max(maxLength, () => t('validation.less_than', { 0: maxLength }))
    .test('unique-name', t('validation.name_already_exists'), function (value) {
      if (!value || !restrictedValues.map((v) => v.toLowerCase()).includes(value.toLowerCase())) {
        return true;
      }
      return false;
    })
    .label(label);

  // Conditionally add the regex validation
  if (patternValidation) {
    schema = schema.matches(patternValidation, t('broadcasts.invalid_characters'));
  }

  return yup.object().shape({ inputField: schema });
};

export const createInputSchema = (maxLength: number) => ({
  required: t('validation.required'),
  maxLength: { value: maxLength, message: t('validation.less_than', { 0: maxLength }) },
  validate: {
    noUrl: (value) => {
      return !noUrlPattern.test(value) || t('validation.url_not_allowed');
    },
  },
});

// URL schema used across multiple schemas
export const urlSchema = yup
  .string()
  .required(() => t('validation.required_field', { 0: 'Url' }))
  .max(LENGTH_XXXL, () => t('validation.less_than', { 0: LENGTH_XXXL }))
  .test({
    test: (value) =>
      urlPattern.test(value) || contextUrl.test(value) || contextVariableWithBraces.test(value),
  });

// Context reference schema
export const contextRefSchema = yup
  .string()
  .matches(contextRefRegExp, { message: () => t('validation.pattern') });

// Context variable value schema
export const contextVariableValueSchema = yup
  .string()
  .max(LENGTH_L)
  .test(
    'context-variable-value',
    () => t('validation.pattern'),
    (value) => {
      if (value === '') {
        return true;
      }

      return contextRefRegExp.test(value);
    }
  );
